// TOPO "Autoral" (modelo_topo = 8): cabeçalho CLARO e sólido do modelo "Autoral".
// Três marcas de identidade que nenhum topo anterior tem:
//  - a LOGO mora num painel próprio, colado na esquerda, separado da barra por
//    uma borda fina (é a assinatura visual do modelo);
//  - o item de menu ATIVO vira um BLOCO SÓLIDO de altura cheia na cor de
//    destaque (var(--cor2)), em vez de sublinhado;
//  - à direita, atalho "Imóveis Favoritos" com o contador do visitante, e uma
//    faixa fina acima com as redes sociais do tenant.
// Fixo no topo com espaçador (sem layout shift). No celular vira logo +
// favoritos + hambúrguer, que reusa o DrawerComponent (via setActiveDrawer).
// TUDO VARIÁVEL: menu = itens do tenant; cores = site.corN com contraste
// adaptativo real (ensureReadableTextColor/isColorLight); logo/redes = dados do
// tenant. Default export = ErrorBoundary com fallback pro Topo padrão.
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { FaFacebook, FaHeart, FaInstagram, FaLinkedin, FaPhoneAlt, FaTiktok, FaYoutube } from 'react-icons/fa';
import { ensureReadableTextColor, favoritosHref, isColorLight, MaskPhone } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { appendCodsiteQuery } from '../../../utils/url';
import { useAuth, useSiteRegiao } from '../../context/Info';
import { temSeletorIdiomas } from '../../../utils/idiomaMoedaSite';
import { IdiomaNavItem } from '../idiomas/menu';
import Topo from '../topo/Topo';
import styles from './TopoAutoral.module.css';

interface ITopoAutoral {
    site: any;
    setActiveDrawer: any;
    fallback?: React.ReactNode;
}

type NavItem = { label: string; href: string; external?: boolean };

// cs2638 Cristiano Domingues (23/09/2026): logo com o DOBRO do tamanho e, no lugar de
// "Imóveis Favoritos" + coração, ícone de telefone + número do tenant — ambos em branco.
// Allowlist por codsite: nos demais sites o topo segue igual.
const TOPO_AUTORAL_LOGO_2X_TELEFONE = new Set<number>([2638]);

// Compara caminhos ignorando query/hash e barra final — é o que decide qual item
// ganha o bloco sólido.
const mesmaRota = (asPath: string, href: string): boolean => {
    const limpa = (v: string) => (v.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/');
    return limpa(asPath) === limpa(href);
};

const TopoAutoralInner: React.FC<ITopoAutoral> = ({ site: siteProp, setActiveDrawer }) => {
    // MULTI-REGIÃO: contatos/dados trocados pelos da região ativa quando houver.
    const site = useSiteRegiao(siteProp);
    const { allDataHome, favorito } = useAuth();
    const router = useRouter();
    const codsite = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;

    // codsite e favoritos só existem no cliente (host/localStorage) — anexa só
    // após montar p/ o HTML do SSR bater com a 1ª render do cliente.
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => setIsMounted(true), []);
    const withCodsite = (path: string) => (isMounted ? appendCodsiteQuery(path, codsite) : path);

    // fundo do topo = cor1 (claro neste modelo) + texto com contraste adaptativo real.
    const corHeader = site?.cor1 || '#ffffff';
    const corHeaderText = ensureReadableTextColor(isColorLight(corHeader) ? site?.cor11 : site?.cor13, corHeader) || '#3c4858';
    // bloco do item ativo = cor de destaque (cor2) + texto calculado sobre ela.
    const corAtivo = site?.cor2 || '#111823';
    const corAtivoText = ensureReadableTextColor(isColorLight(corAtivo) ? site?.cor11 : site?.cor13, corAtivo) || '#ffffff';
    // acento (coração dos favoritos): cor2 se contrastar no header; senão a própria cor do texto.
    const corAcento = ensureReadableTextColor(site?.cor2, corHeader, 2.2, corHeaderText, corHeaderText) || corHeaderText;
    const logoHeight = Number(site?.tamanho_logo) > 1 ? Math.min(Number(site?.tamanho_logo), 84) : 64;

    // menu horizontal: Início + transações + menus disponíveis do tenant + blog.
    const navItems: NavItem[] = [{ label: 'Início', href: '/' }];
    (site?.transacoes || []).forEach((t: string) => {
        if (!t) return;
        const low = String(t).toLowerCase();
        const label = low === 'venda' ? 'Comprar' : low === 'aluguel' ? 'Alugar' : low === 'temporada' ? 'Temporada' : t;
        navItems.push({ label, href: `/imoveis/${low}/-/-/-/-` });
    });
    (allDataHome?.menus || [])
        .filter((m: any) => m?.Disponivel && m?.titulo)
        .slice(0, 5)
        .forEach((m: any) => {
            navItems.push({
                label: m.titulo,
                href: m?.link ? m.link : `/menu/${m.titulo}`,
                external: Boolean(m?.link && !String(m.link).startsWith('/')),
            });
        });
    if (allDataHome?.blogs?.[0]?.id) navItems.push({ label: 'Blog', href: '/blog' });

    const socials = [
        { href: site?.Dados?.instagram, Icon: FaInstagram, rede: 'Instagram' },
        { href: site?.Dados?.facebook, Icon: FaFacebook, rede: 'Facebook' },
        { href: site?.Dados?.youtube, Icon: FaYoutube, rede: 'Youtube' },
        { href: site?.Dados?.tiktok, Icon: FaTiktok, rede: 'Tiktok' },
        { href: site?.Dados?.Linkedin, Icon: FaLinkedin, rede: 'Linkedin' },
    ].filter((s) => s.href);

    const logoSrc = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : '';
    const totalFavoritos = isMounted && Array.isArray(favorito) ? favorito.length : 0;
    const empresa = site?.Dados?.empresa || '';
    const logo2xTelefone = TOPO_AUTORAL_LOGO_2X_TELEFONE.has(Number(site?.id));
    const telefone = site?.Dados?.tel1 || site?.Dados?.tel2 || '';
    const telefoneDigitos = String(telefone).replace(/\D/g, '');

    const telefoneLink = telefoneDigitos ? (
        <a
            href={`tel:+55${telefoneDigitos.replace(/^0/, '')}`}
            className={`${styles.favoritos} ${styles.telefone}`}
            style={{ color: '#ffffff' }}
            title="Ligar para a imobiliária"
            onClick={() => trackMetaEvent(site, 'Contact', { content_name: 'Telefone do topo', content_category: 'header_navigation' })}
        >
            <FaPhoneAlt aria-hidden="true" style={{ color: '#ffffff' }} />
            <span className={styles.telefoneNumero}>{MaskPhone(String(telefone))}</span>
        </a>
    ) : null;

    const favoritosLink = (
        <Link
            prefetch={false}
            href={isMounted ? favoritosHref(favorito || [], codsite) : '/imoveis'}
            className={styles.favoritos}
            style={{ color: corHeaderText }}
            title="Clique aqui para ver seus imóveis favoritos"
        >
            <FaHeart aria-hidden="true" style={{ color: corAcento }} />
            <span className={styles.favoritosLabel}>Imóveis Favoritos</span>
            {totalFavoritos > 0 ? (
                <span className={styles.badge} style={{ background: corAtivo, color: corAtivoText }}>
                    {totalFavoritos}
                </span>
            ) : null}
        </Link>
    );

    return (
        // BUILDER: âncora estável do bloco "topo" (cobre esta variante em qualquer página).
        <div className={`${styles.wrapper}${logo2xTelefone ? ` ${styles.logo2x}` : ''}`} data-builder-section="topo">
            {/* espaçador: mantém o fluxo (header é fixed/fora de fluxo) — evita layout shift. */}
            <div className={styles.spacer} aria-hidden="true" />

            <header className={styles.header} style={{ background: corHeader }}>
                {socials.length > 0 ? (
                    <div className={styles.socialBar}>
                        {socials.map((s, idx) => (
                            <a
                                key={idx}
                                href={s.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Navegar para o ${s.rede} da imobiliária`}
                                className={styles.social}
                                style={{ color: corHeaderText, borderColor: corHeaderText }}
                            >
                                <s.Icon aria-hidden="true" />
                            </a>
                        ))}
                    </div>
                ) : null}

                <div className={styles.inner}>
                    {/* painel da logo — colado na esquerda, separado por uma borda fina */}
                    <Link
                        prefetch={false}
                        href={withCodsite('/')}
                        onClick={() => trackMetaEvent(site, 'ViewContent', { content_name: 'Logo da imobiliária', content_category: 'header_navigation' })}
                        className={styles.logoPanel}
                        style={{ color: corHeaderText }}
                        title={`Imobiliária ${empresa}`}
                    >
                        {logoSrc ? (
                            <Image
                                src={logoSrc}
                                alt={`Imobiliária ${empresa}`}
                                title={`Imobiliária ${empresa}`}
                                className={styles.logo}
                                height={logoHeight}
                                width={260}
                                priority
                                fetchPriority="high"
                                unoptimized
                            />
                        ) : (
                            <span className={styles.logoText} style={{ color: corHeaderText }}>
                                {empresa}
                            </span>
                        )}
                    </Link>

                    {/* menu (desktop) — o item da rota atual vira bloco sólido */}
                    <nav className={styles.nav} aria-label="Menu principal">
                        {navItems.map((item, idx) => {
                            const ativo = !item.external && mesmaRota(router.asPath || '/', item.href);
                            const estilo = ativo
                                ? { background: corAtivo, color: corAtivoText }
                                : { color: corHeaderText };
                            return item.external ? (
                                <a
                                    key={idx}
                                    href={item.href}
                                    className={styles.navLink}
                                    style={estilo}
                                    rel="noopener noreferrer"
                                >
                                    {item.label}
                                </a>
                            ) : (
                                <Link
                                    prefetch={false}
                                    key={idx}
                                    href={withCodsite(item.href)}
                                    className={`${styles.navLink} ${ativo ? styles.navLinkAtivo : ''}`}
                                    style={estilo}
                                    aria-current={ativo ? 'page' : undefined}
                                >
                                    {item.label}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* favoritos (desktop) — no cs2638, telefone no lugar */}
                    <div className={styles.acoes}>
                        {/* 22/10: idioma na barra, junto das ações — rodada 2: fica também no celular (só a bandeira
                            até 900px, ao lado do ☰; a linha da gaveta saiu) */}
                        {temSeletorIdiomas(site) ? (
                            <IdiomaNavItem compactoAte={900} classeBotao={styles.favoritos} estilo={{ color: corHeaderText, marginRight: 14 }} />
                        ) : null}
                        {logo2xTelefone ? telefoneLink : favoritosLink}
                    </div>

                    {/* hambúrguer (mobile) */}
                    <button
                        type="button"
                        className={styles.hamburger}
                        aria-label="Abrir menu"
                        onClick={() => setActiveDrawer(true)}
                        style={{ color: corHeaderText }}
                    >
                        <span className={styles.hamburgerBar} style={{ background: corHeaderText }} />
                        <span className={styles.hamburgerBar} style={{ background: corHeaderText }} />
                        <span className={styles.hamburgerBar} style={{ background: corHeaderText }} />
                    </button>
                </div>
            </header>
        </div>
    );
};

// ErrorBoundary → fallback pro Topo padrão (mesmo padrão do TopoEditorial).
class TopoAutoral extends React.Component<ITopoAutoral, { hasError: boolean }> {
    constructor(props: ITopoAutoral) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: o topo degrada pro padrão sem quebrar a página.
    }
    render() {
        const { site, setActiveDrawer, fallback } = this.props;
        const defaultTopo = fallback ?? <Topo site={site} setActiveDrawer={setActiveDrawer} />;
        if (this.state.hasError || !site) return defaultTopo;
        return <TopoAutoralInner {...this.props} />;
    }
}

export default TopoAutoral;
